// src/components/EventSlider.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import EventLanding from './EventLanding';
import { getEvents as getSubgraphEvents } from '../services/subgraph';
import './EventSlider.css';

const CACHE_KEY = 'events_cache';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
const FETCH_TIMEOUT = 10000; // 10 seconds
const AUTO_ROTATE_MS = 5000;

// Get configured contract address from env
const CONFIGURED_CONTRACT = process.env.REACT_APP_EVENT_TICKET_ADDRESS?.toLowerCase() || '';
const CONTRACT_ADDRESS = process.env.REACT_APP_EVENT_TICKET_WITH_EVENTS_ADDRESS ||
  process.env.REACT_APP_EVENT_TICKET_ADDRESS || '';
const CHAIN_ID = Number(process.env.REACT_APP_CHAIN_ID || 84532);

// Filter events by configured contract address (lenient: falls back to all if none match)
const filterByContract = (events) => {
  if (!CONFIGURED_CONTRACT) {
    return events;
  }
  const filtered = events.filter(e =>
    e.contractAddress?.toLowerCase() === CONFIGURED_CONTRACT
  );
  if (filtered.length === 0 && events.length > 0) {
    console.warn(`[EventSlider] No events match contract ${CONFIGURED_CONTRACT}, showing all ${events.length} events`);
    return events;
  }
  return filtered;
};

// Clear cache if contract address changed
const CACHE_CONTRACT_KEY = 'events_cache_contract';
try {
  const storedContract = localStorage.getItem(CACHE_CONTRACT_KEY);
  if (CONFIGURED_CONTRACT && storedContract && storedContract !== CONFIGURED_CONTRACT) {
    localStorage.removeItem(CACHE_KEY);
  }
  if (CONFIGURED_CONTRACT) {
    localStorage.setItem(CACHE_CONTRACT_KEY, CONFIGURED_CONTRACT);
  }
} catch (e) {
  // localStorage not available
}

// RPC for contract fallback reads
const RPC_URL = process.env.REACT_APP_ALCHEMY_RPC_URL ||
  (process.env.REACT_APP_ALCHEMY_API_KEY
    ? `https://base-mainnet.g.alchemy.com/v2/${process.env.REACT_APP_ALCHEMY_API_KEY}`
    : 'https://mainnet.base.org');

// Enrich events that have empty imageURI by reading from contract
const enrichWithContractImages = async (events) => {
  const needsImage = events.filter(e => !e.image && e.eventId);
  if (needsImage.length === 0) return events;

  console.log(`[EventSlider] Reading imageURI from contract for ${needsImage.length} events...`);

  try {
    const provider = new ethers.JsonRpcProvider(RPC_URL);
    const contract = new ethers.Contract(CONTRACT_ADDRESS, [
      'function events(uint256) view returns (uint256, string, string, string, string, uint256, uint256, address, bool, string)'
    ], provider);

    await Promise.all(needsImage.map(async (e) => {
      try {
        const info = await contract.events(e.eventId);
        if (info[4]) { // imageURI at index 4
          e.image = info[4];
        }
        if (!e.description && info[2]) { // description at index 2
          e.description = info[2];
        }
      } catch (err) {
        // ignore — will use fallback/no image
      }
    }));
  } catch (err) {
    console.warn('[EventSlider] Contract fallback failed:', err.message);
  }

  return events;
};

// Validate minimum event shape
const isValidEvent = (e) => e && typeof e === 'object' && e.id && e.date;

// Fetch with timeout
const fetchWithTimeout = (url, timeoutMs = FETCH_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timeoutId));
};

// Parse and sort events (pure function, no parsedDate storage in cache)
const processEvents = (rawList) => {
  return filterByContract(rawList)
    .filter(e => isValidEvent(e) && e.active !== false)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

// Find the index of the next upcoming event
const findUpcomingIndex = (events) => {
  const now = Date.now();
  const idx = events.findIndex(e => new Date(e.date).getTime() >= now);
  return idx >= 0 ? idx : 0;
};

export default function EventSlider() {
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMockMode = process.env.REACT_APP_MOCK_EVENTS === 'true';
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  // Reset auto-rotation timer
  const resetAutoRotate = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (events.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % events.length);
    }, AUTO_ROTATE_MS);
  }, [events.length]);

  // Load events from subgraph (on-chain events with correct contract address)
  const loadFromSubgraph = async () => {
    try {
      const sgEvents = await getSubgraphEvents({ first: 200, activeOnly: true });
      if (!sgEvents || sgEvents.length === 0) return null;

      // Load localStorage supplement for extra fields (image, category, venue, etc.)
      let supplement = {};
      try { supplement = JSON.parse(localStorage.getItem('admin_events_supplement') || '{}'); } catch {}

      const mapped = sgEvents.map(e => {
        const sup = supplement[String(e.eventId)] || supplement[String(e.id)] || {};
        return {
          id: e.eventId || e.id,
          eventId: Number(e.eventId),
          name: e.name,
          description: e.description || sup.description || '',
          location: e.location,
          venue: sup.venue || e.location || '',
          image: sup.image || e.imageURI || '',
          date: new Date(Number(e.startTime) * 1000).toISOString(),
          startTime: Number(e.startTime),
          endTime: Number(e.endTime),
          organizer: e.organizer,
          active: e.active,
          contractAddress: CONTRACT_ADDRESS,
          chainId: CHAIN_ID,
          capacity: sup.capacity || 0,
          category: sup.category || '',
          venueCoordinates: sup.venueCoordinates || null,
          ticketTypes: (e.ticketTypes || []).map(tt => ({
            id: Number(tt.id || tt.eventId),
            name: tt.name,
            price: String(tt.price),
            maxSupply: String(tt.maxSupply),
            totalMinted: Number(tt.totalMinted || 0),
            saleStart: Number(tt.saleStart || 0),
            saleEnd: Number(tt.saleEnd || 0),
          })),
          _source: 'subgraph',
        };
      });

      // Enrich events with empty imageURI by reading from contract
      return enrichWithContractImages(mapped);
    } catch (err) {
      console.warn('[EventSlider] Subgraph unavailable:', err.message);
      return null;
    }
  };

  const loadEvents = useCallback(async () => {
    if (isMockMode) {
      const MOCK_EVENTS = [
        { id: "event-1", name: "Pregame Launch Party", date: "2026-10-25T18:00:00Z", image: "/events/pregame.png", contractAddress: CONTRACT_ADDRESS || "0x31fb6b252A4140988EFd67bf84727dFD2B1d2721", chainId: CHAIN_ID, description: "The ultimate pregame experience on Base.", active: true },
        { id: "event-2", name: "Base Fest 2026", date: "2026-11-15T20:00:00Z", image: "/events/basefest.png", contractAddress: CONTRACT_ADDRESS || "0x31fb6b252A4140988EFd67bf84727dFD2B1d2721", chainId: CHAIN_ID, description: "Music, tech, and community on Base.", active: true },
      ];
      setEvents(MOCK_EVENTS);
      setCurrentIndex(0);
      setLoading(false);
      return;
    }

    try {
      // 1. Try subgraph first (on-chain events with correct contract address)
      const sgEvents = await loadFromSubgraph();

      if (sgEvents && sgEvents.length > 0) {
        const activeEvents = processEvents(sgEvents);
        if (mountedRef.current) {
          setEvents(activeEvents);
          setCurrentIndex(findUpcomingIndex(activeEvents));
        }
        // Cache subgraph data
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data: sgEvents, timestamp: Date.now(), source: 'subgraph' }));
        } catch {}
        return;
      }

      // 2. Try cache (subgraph data only)
      let cacheData = null;
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) cacheData = JSON.parse(cached);
      } catch {
        localStorage.removeItem(CACHE_KEY);
      }

      if (cacheData?.data && Array.isArray(cacheData.data) && Date.now() - cacheData.timestamp < CACHE_EXPIRY) {
        const activeEvents = processEvents(cacheData.data);
        if (mountedRef.current) {
          setEvents(activeEvents);
          setCurrentIndex(findUpcomingIndex(activeEvents));
        }
        return;
      }

      // No events.json fallback — on-chain data only
      throw new Error('No on-chain events found. Subgraph may still be syncing.');
    } catch (err) {
      console.error('[EventSlider] Failed to load events:', err);
      if (mountedRef.current) {
        setError(err.name === 'AbortError'
          ? 'Loading events timed out. Please refresh the page.'
          : 'Failed to load event list. Check console for details.');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [isMockMode]);

  // Load on mount, cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    loadEvents();
    return () => { mountedRef.current = false; };
  }, [loadEvents]);

  // Auto-rotate every 5s if multiple events
  useEffect(() => {
    resetAutoRotate();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [resetAutoRotate]);

  // Navigation (resets auto-rotation timer)
  const goToPrev = () => {
    setCurrentIndex(prev => (prev === 0 ? events.length - 1 : prev - 1));
    resetAutoRotate();
  };
  const goToNext = () => {
    setCurrentIndex(prev => (prev + 1) % events.length);
    resetAutoRotate();
  };
  const goToIndex = (i) => {
    setCurrentIndex(i);
    resetAutoRotate();
  };

  if (loading) return <div className="event-slider-loading">Loading events...</div>;
  if (error) {
    return (
      <div className="event-slider-error">
        {error}
        <br />
        <button
          onClick={() => { setError(null); setLoading(true); loadEvents(); }}
          style={{ marginTop: 8, padding: '6px 16px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="event-slider-error">
        No events found for the configured contract.
        <br />
        <small style={{ opacity: 0.7 }}>Contract: {CONFIGURED_CONTRACT || 'not set'}</small>
      </div>
    );
  }

  const currentEvent = events[currentIndex];
  const total = events.length;

  return (
    <div className="event-slider">
      {total > 1 && (
        <div className="slider-bar">
          <button className="slider-bar-btn" onClick={goToPrev} aria-label="Previous event">
            <span className="slider-bar-arrow">&#8249;</span>
          </button>
          <div className="slider-bar-center">
            <div className="slider-bar-dots">
              {events.map((_, i) => (
                <span
                  key={i}
                  className={`slider-bar-dot ${i === currentIndex ? 'active' : ''}`}
                  onClick={() => goToIndex(i)}
                />
              ))}
            </div>
            <span className="slider-bar-counter">{currentIndex + 1} / {total}</span>
          </div>
          <button className="slider-bar-btn" onClick={goToNext} aria-label="Next event">
            <span className="slider-bar-arrow">&#8250;</span>
          </button>
        </div>
      )}
      <EventLanding event={currentEvent} />
    </div>
  );
}
